import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp, api } from "@/context/AppContext";
import { Store, MessageCircle, ShoppingBag } from "lucide-react";

export default function Admin() {
  const { lang, t, owner } = useApp();
  const navigate = useNavigate();
  const head = lang === "hi" ? "font-hi" : "font-display";
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!owner) { navigate("/login"); return; }
    if (!owner.is_admin) { navigate("/dashboard"); return; }
    api.get("/admin/stores").then((r) => setRows(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Failed"));
  }, [owner, navigate]);

  if (!owner) return null;
  if (err) return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-[#9B2226]">{err}</div>;
  if (!rows) return <div className="max-w-7xl mx-auto px-4 py-24 text-center text-[#635057]">{t.common.loading}</div>;

  const totals = rows.reduce(
    (acc, r) => ({
      sarees: acc.sarees + (r.saree_count || 0),
      tryons: acc.tryons + (r.tryon_count || 0),
      buys: acc.buys + (r.buy_click_count || 0),
    }),
    { sarees: 0, tryons: 0, buys: 0 }
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10 lg:py-16" data-testid="admin-page">
      <div className="text-xs tracking-[0.3em] uppercase font-semibold text-[#C9A84C]">✦ Super Admin</div>
      <h1 className={`${head} text-3xl sm:text-5xl text-[#2D1820] font-light tracking-tight mt-2`}>All stores</h1>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Stores" value={rows.length} icon={Store} />
        <KPI label="Sarees" value={totals.sarees} icon={ShoppingBag} />
        <KPI label="Try-ons" value={totals.tryons} icon={null} accent />
        <KPI label="Buy clicks" value={totals.buys} icon={MessageCircle} />
      </div>

      <div className="mt-10 overflow-x-auto border border-[#E8DFDC]">
        <table className="w-full text-sm" data-testid="admin-stores-table">
          <thead className="bg-[#FCFAF8] text-[10px] tracking-[0.25em] uppercase text-[#635057] font-semibold">
            <tr>
              <th className="text-left p-4">Store</th>
              <th className="text-left p-4 hidden md:table-cell">Email</th>
              <th className="text-right p-4">Sarees</th>
              <th className="text-right p-4">Try-ons</th>
              <th className="text-right p-4">Buys</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#E8DFDC] hover:bg-[#FCFAF8]" data-testid={`admin-store-row-${r.id}`}>
                <td className="p-4">
                  <div className="font-medium text-[#2D1820]">{r.store_name}</div>
                  <div className="text-[11px] text-[#635057] mt-1">{r.store_slug}</div>
                </td>
                <td className="p-4 hidden md:table-cell text-[#635057]">{r.email}</td>
                <td className="p-4 text-right font-medium">{r.saree_count}</td>
                <td className="p-4 text-right font-medium text-[#6B1F3A]">{r.tryon_count}</td>
                <td className="p-4 text-right font-medium">{r.buy_click_count}</td>
                <td className="p-4 text-right">
                  <Link to={`/store/${r.store_slug}`} className="text-[11px] tracking-widest uppercase text-[#6B1F3A] hover:text-[#C9A84C]">
                    View →
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KPI({ label, value, accent }) {
  return (
    <div className={`border border-[#E8DFDC] p-5 ${accent ? "bg-[#6B1F3A] text-white" : "bg-white"}`}>
      <div className={`text-[10px] tracking-[0.25em] uppercase font-semibold ${accent ? "text-[#C9A84C]" : "text-[#635057]"}`}>{label}</div>
      <div className="font-display text-4xl font-light mt-2">{value}</div>
    </div>
  );
}
